import { useQuery } from "@apollo/client";
import { STATE_QUERY } from "../apis/taxonomy/queries";
import { useTaxonomyContext } from "../contexts/Taxonomy/TaxonomyContext";
import { HgCountry, HgState } from "../types/taxonomy";
import { TAXONOMY_CLIENT } from "../utils/consts";
import { generateFilterLabel } from "../utils/filters/utils";

const generateCountriesLabel = (
  selectedIds: string[],
  selectedExcludedIds: string[],
  countries: HgCountry[]
) => {
  const countriesOptions = countries.map((country) => ({
    id: country?.alpha2 || "",
    name: country?.name || "",
  }));

  return generateFilterLabel(
    selectedIds,
    selectedExcludedIds,
    countriesOptions,
    "country"
  );
};

const generateStatesLabel = (
  countryAlpha2s: string,
  stateIds: string[],
  states: HgState[],
  countries: HgCountry[]
) => {
  const country = countries.find(
    (country) => country?.alpha2 === countryAlpha2s
  );

  if (!country) return "";

  if (stateIds.length === 1) {
    const state = states.find((state) => state?.id === stateIds[0]);
    if (state) {
      return `${state.name}, ${country.name}`;
    }
  } else if (stateIds.length > 1) {
    return `${stateIds.length} ${country.name} state/provinces`;
  }

  return "";
};

const generateRegionsLabel = (
  selectedRegions: string[],
  selectedExcludedCountryIds: string[],
  countries: HgCountry[]
) => {
  let regionLabel = "";
  const sortedRegions = selectedRegions.sort((a, b) => a.localeCompare(b));

  // Generate region label based on selected regions
  if (sortedRegions.length === 1) {
    [regionLabel] = sortedRegions;
  } else if (sortedRegions.length > 1) {
    const lastRegion = sortedRegions[sortedRegions.length - 1];
    const otherRegions = sortedRegions.slice(0, -1);
    regionLabel = `${otherRegions.join(", ")} or ${lastRegion}`;
  }

  // Handle excluded countries
  if (selectedExcludedCountryIds.length > 0 && regionLabel !== "") {
    const excludedLabel = generateCountriesLabel(
      [],
      selectedExcludedCountryIds,
      countries
    );
    return `${regionLabel} included, ${excludedLabel}`;
  }

  // If no excluded countries, just return the region label
  if (regionLabel !== "") return regionLabel;

  // If no regions but have excluded countries, use generateCountriesLabel
  if (selectedExcludedCountryIds.length > 0) {
    return generateCountriesLabel([], selectedExcludedCountryIds, countries);
  }

  return "";
};

interface useGeographyLabelProps {
  countryAlpha2s?: string[];
  excludedCountryAlpha2s?: string[];
  regionNames?: string[];
  stateIds?: string[];
}

export const useGeographyLabel = ({
  countryAlpha2s,
  excludedCountryAlpha2s,
  regionNames,
  stateIds,
}: useGeographyLabelProps) => {
  const { countries } = useTaxonomyContext();

  const { data: statesData, loading: statesLoading } = useQuery(STATE_QUERY, {
    context: { clientName: TAXONOMY_CLIENT },
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
    variables: {
      ids: stateIds,
    },
    skip: stateIds?.length === 0,
  });

  const states = statesData?.state?.data as HgState[];

  if (countryAlpha2s?.length && stateIds?.length) {
    if (statesLoading) {
      return "Loading...";
    }

    return generateStatesLabel(countryAlpha2s[0], stateIds, states, countries);
  }

  if (regionNames?.length) {
    return generateRegionsLabel(
      regionNames || [],
      excludedCountryAlpha2s || [],
      countries
    );
  }

  if (countryAlpha2s?.length || excludedCountryAlpha2s?.length) {
    return generateCountriesLabel(
      countryAlpha2s || [],
      excludedCountryAlpha2s || [],
      countries
    );
  }

  return "";
};
